import React from 'react';
import { Save, Check, RefreshCcw } from 'lucide-react';
import PriceDistributionView from '../PriceDistributionView';
import { formatConfidencePercent } from '../../utils/formatters';

// Single analyzed item card: manual name, estimated value/distribution,
// confidence, editable actual price and condition / AI-source badges.
const ResultItemCard = ({
  item,
  index,
  originalPrices,
  originalNames,
  updatingId,
  updatingNameId,
  onPriceChange,
  onManualNameChange,
  onSavePrice,
  onSaveItemName,
}) => {
  const isDirty = item.id && originalPrices[item.id] !== undefined && parseFloat(originalPrices[item.id]) !== parseFloat(item.actual_price);
  const isUpdating = updatingId === item.id;
  const isNameDirty = item.id && originalNames[item.id] !== undefined && originalNames[item.id] !== (item.manual_name || '');
  const isNameUpdating = updatingNameId === item.id;
  const condition = item?.condition || 'Unknown';
  const priceSource = item?.price_source || 'model_knowledge';
  const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
  const sourceBg = priceSource === 'ebay_rag' ? 'bg-teal/10 text-teal' : priceSource === 'image' ? 'bg-warn/10 text-warn' : 'bg-surface-2 text-muted';
  const condBg = condition === 'New' ? 'bg-positive/10 text-positive' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-danger/10 text-danger' : condition === 'Unknown' ? 'bg-surface-2 text-muted' : 'bg-warn/10 text-warn';

  return (
    <div className="bg-surface rounded border border-hairline p-3 shadow-sm">
      <strong className="block text-ink text-base mb-1">{item.item || `Item ${index + 1}`}</strong>

      <div className="flex flex-col gap-1 mb-2">
        <span className="text-muted text-xs">Manual Name:</span>
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Enter custom name..."
            className={`w-full pr-8 py-1 px-2 border rounded text-sm text-ink focus:outline-none focus:ring-1 transition-all ${
              isNameDirty ? 'border-accent focus:ring-accent bg-accent/5' : 'border-hairline focus:ring-positive'
            }`}
            value={item.manual_name || ''}
            onChange={(e) => onManualNameChange(item.id, e.target.value)}
          />
          {item.id && (
            <button
              onClick={() => onSaveItemName(item.id, item.manual_name || '')}
              disabled={!isNameDirty || isNameUpdating}
              className={`absolute right-1 p-1 rounded transition-all ${
                isNameUpdating ? 'text-muted' : isNameDirty ? 'text-accent hover:bg-accent/10' : 'text-positive cursor-default'
              }`}
            >
              {isNameUpdating ? <RefreshCcw size={14} className="animate-spin" /> : isNameDirty ? <Save size={14} /> : <Check size={14} />}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm text-ink">
          Estimated Value: <span className="font-bold text-positive">{Number.isFinite(Number(item.estimated_value)) ? `$${Number(item.estimated_value).toFixed(2)}` : 'n/a'}</span>
        </p>
        <PriceDistributionView
          rangeValue={item?.estimated_value_range}
          distributionValue={item?.price_estimate_distribution}
        />
        <p className="text-sm text-ink">
          Confidence: <span className="font-bold text-ai">{formatConfidencePercent(item?.confidence_score)}</span>
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted">Actual Price:</span>
          <div className="relative flex items-center">
            <span className="absolute left-2 text-muted text-sm">$</span>
            <input
              type="number"
              placeholder="0.00"
              value={item.actual_price}
              onChange={(e) => onPriceChange(item.id, e.target.value)}
              className={`w-24 pl-5 pr-8 py-1 border rounded text-sm text-ink focus:outline-none focus:ring-1 transition-all ${
                isDirty ? 'border-accent focus:ring-accent bg-accent/5' : 'border-hairline focus:ring-positive'
              }`}
              step="0.01"
            />
            {item.id && (
              <button
                onClick={() => onSavePrice(item.id, item.actual_price)}
                disabled={!isDirty || isUpdating}
                className={`absolute right-1 p-1 rounded transition-all ${
                  isUpdating ? 'text-muted' : isDirty ? 'text-accent hover:bg-accent/10' : 'text-positive cursor-default'
                }`}
              >
                {isUpdating ? <RefreshCcw size={14} className="animate-spin" /> : isDirty ? <Save size={14} /> : <Check size={14} />}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <span className="text-[9px] text-muted">Condition:</span>
        <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span>
        <span className="text-[9px] text-muted ml-1">AI Src:</span>
        <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span>
      </div>

      {item.explanation && (
        <div className="mt-2 text-xs text-ink bg-surface-2 border border-hairline rounded p-2 leading-relaxed">
          {item.explanation}
        </div>
      )}
    </div>
  );
};

export default ResultItemCard;
